/* ══ HS!/JPD — LIVE JEAN PREVIEW ══════════════════════════════════════
   Draws the jean the customer is actually specifying: the silhouette
   comes from the CUT, the fabric fill from the DENIM, the topstitch from
   the THREAD, and every add-on drops a pin where the work will land.

   WHY PROCEDURAL: there are no fabric swatch photos in Shopify yet, and
   a photo-per-combination library explodes combinatorially (8 cuts x 5
   fabrics x 4 threads = 160 shots before a single add-on). So the weave
   is generated in SVG from the fabric's colour.

   THE UPGRADE PATH — this is the important part:
     custom.swatch_image EMPTY  -> generated twill weave (works today)
     custom.swatch_image SET    -> that photo is tiled as the fill instead
   Uploading a swatch photo in Admin silently upgrades the preview to the
   real cloth. No code change, no redeploy. Same for cut_config.shape:
   set it in Admin to tune a silhouette without touching this file.

   HARD RULE: zero backslash characters anywhere in this file — the MCP
   deploy transport corrupts them. No regex escapes, no escape sequences.
   ─────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  /* ── silhouette presets ────────────────────────────────────────────
     Units are the 200 x 336 garment viewBox.
       hip   = half-width of the garment at the hip
       kneeW = width of ONE leg at the knee
       hemW  = width of ONE leg at the hem  (hemW < kneeW == taper)
       hemC  = distance from centre to the hem centre-line (stance)
       rise  = y of the crotch point
     A cut picks its preset by keyword in its title/SKU. cut_config.shape
     overrides it entirely, so a new cut is tunable from Admin.        */
  var SHAPES = {
    SKINNY:   { hip: 54, kneeW: 40, hemW: 30, hemC: 28, rise: 90 },
    SLIM:     { hip: 57, kneeW: 50, hemW: 42, hemC: 30, rise: 90 },
    TAPER:    { hip: 57, kneeW: 52, hemW: 40, hemC: 30, rise: 90 },
    STRAIGHT: { hip: 59, kneeW: 54, hemW: 52, hemC: 31, rise: 92 },
    BOOT:     { hip: 58, kneeW: 50, hemW: 60, hemC: 33, rise: 92 },
    WIDE:     { hip: 64, kneeW: 66, hemW: 72, hemC: 37, rise: 98 },
    BARREL:   { hip: 66, kneeW: 60, hemW: 48, hemC: 32, rise: 98 },
    DEFAULT:  { hip: 57, kneeW: 50, hemW: 42, hemC: 30, rise: 90 }
  };
  var SHAPE_ORDER = ['SKINNY', 'BARREL', 'BOOT', 'WIDE', 'STRAIGHT', 'TAPER', 'SLIM'];

  /* ── fabric tone presets ───────────────────────────────────────────
     base = cloth colour, hi/lo = twill rib highlight + shadow,
     wash = 0 raw .. 1 heavily washed (drives whiskers + fade).        */
  var FABRICS = {
    BLACK:  { base: '#23242a', hi: '#3b3d47', lo: '#131318', wash: 0.05 },
    WASHED: { base: '#6b8fc4', hi: '#93b4e0', lo: '#4d6d9c', wash: 0.60 },
    HEAVY:  { base: '#263d67', hi: '#3c578d', lo: '#19294a', wash: 0.00 },
    ECRU:   { base: '#ded3bb', hi: '#f0e8d6', lo: '#c0b296', wash: 0.20 },
    DEAD:   { base: '#3a5687', hi: '#5673ab', lo: '#283c61', wash: 0.18 },
    RAW:    { base: '#2f4a7a', hi: '#4966a4', lo: '#213558', wash: 0.00 }
  };

  /* ── add-on pin spots, by face ─────────────────────────────────────
     Keyword in the add-on title decides where its pin lands. Anything
     unmatched lands on the thigh so it is never silently dropped.     */
  var SPOTS = {
    front: {
      WAIST:  [100, 20],  BELT: [100, 20],
      FLY:    [100, 70],  COIN: [127, 44],
      POCKET: [68, 56],   KNEE: [68, 186],
      HEM:    [70, 306],  CUFF: [70, 306],
      SELVEDGE: [132, 240],
      DEFAULT: [70, 130]
    },
    back: {
      WAIST:  [100, 20],  BELT: [100, 20],
      YOKE:   [100, 50],
      POCKET: [70, 82],   PATCH: [130, 82],
      KNEE:   [68, 186],
      HEM:    [70, 306],  CUFF: [70, 306],
      SEAT:   [100, 112],
      DEFAULT: [100, 82]
    }
  };

  var uid = 0;

  function has(text, needle) {
    return String(text || '').toUpperCase().indexOf(needle) !== -1;
  }

  function pickShape(title, sku, config) {
    if (config && config.shape && typeof config.shape === 'object') {
      var s = {}, k;
      for (k in SHAPES.DEFAULT) { s[k] = SHAPES.DEFAULT[k]; }
      for (k in config.shape) {
        if (typeof config.shape[k] === 'number') { s[k] = config.shape[k]; }
      }
      return s;
    }
    var hay = String(title || '') + ' ' + String(sku || '');
    for (var i = 0; i < SHAPE_ORDER.length; i++) {
      if (has(hay, SHAPE_ORDER[i])) { return SHAPES[SHAPE_ORDER[i]]; }
    }
    return SHAPES.DEFAULT;
  }

  function pickFabric(title, config) {
    var tone;
    if (has(title, 'BLACK')) { tone = FABRICS.BLACK; }
    else if (has(title, 'WASH')) { tone = FABRICS.WASHED; }
    else if (has(title, 'ECRU') || has(title, 'NATURAL')) { tone = FABRICS.ECRU; }
    else if (has(title, 'DEADSTOCK')) { tone = FABRICS.DEAD; }
    else if (has(title, '16OZ') || has(title, 'HEAVY')) { tone = FABRICS.HEAVY; }
    else { tone = FABRICS.RAW; }

    var out = { base: tone.base, hi: tone.hi, lo: tone.lo, wash: tone.wash };
    /* Admin overrides — a new fabric needs no code change. */
    if (config) {
      if (typeof config.color === 'string' && config.color.charAt(0) === '#') {
        out.base = config.color;
        out.hi = shift(config.color, 26);
        out.lo = shift(config.color, -26);
      }
      if (typeof config.wash === 'number') { out.wash = config.wash; }
    }
    return out;
  }

  /* Lighten (amount > 0) or darken a hex colour. No regex — the deploy
     transport eats backslashes, and every regex here would need them. */
  function shift(hex, amount) {
    var h = String(hex).replace('#', '');
    if (h.length === 3) { h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2]; }
    var num = parseInt(h, 16);
    if (isNaN(num)) { return hex; }
    var r = clamp255(((num >> 16) & 255) + amount);
    var g = clamp255(((num >> 8) & 255) + amount);
    var b = clamp255((num & 255) + amount);
    return 'rgb(' + r + ',' + g + ',' + b + ')';
  }
  function clamp255(v) { return v < 0 ? 0 : (v > 255 ? 255 : Math.round(v)); }

  /* ── the silhouette ────────────────────────────────────────────────
     Built as one closed path, mirror-symmetric about x = 100 by
     construction: every right-hand point is 200 minus its left twin. */
  var GEO = { cx: 100, waistY: 6, hipY: 62, kneeY: 186, hemY: 320 };

  function legs(s) {
    var cx = GEO.cx;
    var legTopC = cx - s.hip * 0.5;
    var legHemC = cx - s.hemC;
    var legKneeC = legTopC + (legHemC - legTopC) * 0.62;
    /* Clamp so the two legs can never cross the centre line or run off
       the canvas — a hand-typed cut_config.shape in Admin must not be
       able to produce a broken drawing. */
    return {
      kOut: Math.max(legKneeC - s.kneeW / 2, 6),
      kIn: Math.min(legKneeC + s.kneeW / 2, cx - 1),
      hOut: Math.max(legHemC - s.hemW / 2, 6),
      hIn: Math.min(legHemC + s.hemW / 2, cx - 1)
    };
  }

  function bodyPath(s) {
    var cx = GEO.cx, waistY = GEO.waistY, hipY = GEO.hipY, kneeY = GEO.kneeY, hemY = GEO.hemY;
    var waist = s.hip - 11;
    var hip = s.hip;
    var rise = s.rise;

    var L = legs(s);
    var kOut = L.kOut, kIn = L.kIn, hOut = L.hOut, hIn = L.hIn;

    var M = function (v) { return 200 - v; };
    var p = [];
    p.push('M' + (cx - waist) + ',' + waistY);
    /* left outer: waist -> hip -> knee -> hem */
    p.push('C' + (cx - hip) + ',' + (waistY + 24) + ' ' + (cx - hip) + ',' + (hipY - 10) + ' ' + (cx - hip + 1) + ',' + hipY);
    p.push('C' + (cx - hip + 1) + ',' + (hipY + 46) + ' ' + (kOut - 2) + ',' + (kneeY - 60) + ' ' + kOut + ',' + kneeY);
    p.push('C' + (kOut + 1) + ',' + (kneeY + 58) + ' ' + hOut + ',' + (hemY - 54) + ' ' + hOut + ',' + hemY);
    /* left hem */
    p.push('L' + hIn + ',' + hemY);
    /* left inner: hem -> knee -> crotch */
    p.push('C' + hIn + ',' + (hemY - 54) + ' ' + (kIn - 1) + ',' + (kneeY + 58) + ' ' + kIn + ',' + kneeY);
    p.push('C' + (kIn + 2) + ',' + (kneeY - 62) + ' ' + (cx - 4) + ',' + (rise + 34) + ' ' + (cx - 3) + ',' + rise);
    /* crotch */
    p.push('Q' + cx + ',' + (rise + 7) + ' ' + (cx + 3) + ',' + rise);
    /* right inner: crotch -> knee -> hem */
    p.push('C' + M(cx - 4) + ',' + (rise + 34) + ' ' + M(kIn + 2) + ',' + (kneeY - 62) + ' ' + M(kIn) + ',' + kneeY);
    p.push('C' + M(kIn - 1) + ',' + (kneeY + 58) + ' ' + M(hIn) + ',' + (hemY - 54) + ' ' + M(hIn) + ',' + hemY);
    /* right hem */
    p.push('L' + M(hOut) + ',' + hemY);
    /* right outer: hem -> knee -> hip -> waist */
    p.push('C' + M(hOut) + ',' + (hemY - 54) + ' ' + M(kOut + 1) + ',' + (kneeY + 58) + ' ' + M(kOut) + ',' + kneeY);
    p.push('C' + M(kOut - 2) + ',' + (kneeY - 60) + ' ' + M(cx - hip + 1) + ',' + (hipY + 46) + ' ' + M(cx - hip + 1) + ',' + hipY);
    p.push('C' + M(cx - hip) + ',' + (hipY - 10) + ' ' + M(cx - hip) + ',' + (waistY + 24) + ' ' + M(cx - waist) + ',' + waistY);
    p.push('Z');
    return p.join(' ');
  }

  /* ── the fill ──────────────────────────────────────────────────────
     A real swatch photo wins when one exists; otherwise a twill weave
     is generated from the fabric colour. Same <pattern> id either way,
     so nothing downstream cares which branch ran.                     */
  function fillPattern(id, fab, swatchUrl) {
    if (swatchUrl) {
      return '<pattern id="' + id + '" width="64" height="64" patternUnits="userSpaceOnUse">' +
             '<image href="' + swatchUrl + '" x="0" y="0" width="64" height="64" preserveAspectRatio="xMidYMid slice"></image>' +
             '</pattern>';
    }
    /* 8x8 tile of 63-degree twill rib — the diagonal that reads as denim */
    return '<pattern id="' + id + '" width="8" height="8" patternUnits="userSpaceOnUse">' +
           '<rect width="8" height="8" fill="' + fab.base + '"></rect>' +
           '<path d="M-2,2 L2,-2 M0,8 L8,0 M6,10 L10,6" stroke="' + fab.hi + '" stroke-width="1.7" opacity="0.55"></path>' +
           '<path d="M-2,5 L5,-2 M3,8 L8,3" stroke="' + fab.lo + '" stroke-width="1" opacity="0.4"></path>' +
           '</pattern>';
  }

  function defs(id, fab, swatchUrl, body) {
    var d = [];
    d.push(fillPattern('hsp-fill-' + id, fab, swatchUrl));
    /* slub / cotton noise */
    d.push('<filter id="hsp-slub-' + id + '"><feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="3" seed="7"></feTurbulence>' +
           '<feColorMatrix type="saturate" values="0"></feColorMatrix></filter>');
    /* soft edge shading so it reads as cloth, not a sticker */
    d.push('<linearGradient id="hsp-shade-' + id + '" x1="0" y1="0" x2="1" y2="0">' +
           '<stop offset="0" stop-color="#000" stop-opacity="0.30"></stop>' +
           '<stop offset="0.18" stop-color="#000" stop-opacity="0.04"></stop>' +
           '<stop offset="0.5" stop-color="#fff" stop-opacity="0.06"></stop>' +
           '<stop offset="0.82" stop-color="#000" stop-opacity="0.04"></stop>' +
           '<stop offset="1" stop-color="#000" stop-opacity="0.30"></stop></linearGradient>');
    /* whiskers / honeycomb fade — only visible on washed cloth */
    d.push('<radialGradient id="hsp-fade-' + id + '"><stop offset="0" stop-color="#fff" stop-opacity="0.55"></stop>' +
           '<stop offset="1" stop-color="#fff" stop-opacity="0"></stop></radialGradient>');
    d.push('<clipPath id="hsp-clip-' + id + '"><path d="' + body + '"></path></clipPath>');
    return '<defs>' + d.join('') + '</defs>';
  }

  /* ── detail lines: seams, topstitch, pockets, hardware ─────────────── */
  function details(s, face, stitch, hardware) {
    var cx = GEO.cx;
    var hip = s.hip, waist = s.hip - 11, rise = s.rise;
    var g = [];
    var st = ' stroke="' + stitch + '" fill="none" stroke-linecap="round" ';
    var dash = ' stroke-dasharray="4 2.6" ';

    /* waistband — both faces. Control points pulled INSIDE the endpoints
       so the band dips gently instead of bulging out at the side seams. */
    g.push('<path d="M' + (cx - waist - 1) + ',26 C' + (cx - 38) + ',30 ' + (cx + 38) + ',30 ' + (cx + waist + 1) + ',26"' + st + dash + ' stroke-width="1.6"></path>');
    g.push('<path class="hsp-ink" d="M' + (cx - waist - 2) + ',30 C' + (cx - 38) + ',34 ' + (cx + 38) + ',34 ' + (cx + waist + 2) + ',30" fill="none" stroke-width="1.4"></path>');
    /* belt loops */
    g.push('<path class="hsp-ink" d="M' + (cx - 30) + ',8 v22 M' + (cx + 30) + ',8 v22 M' + cx + ',8 v22" fill="none" stroke-width="2.6"></path>');

    if (face === 'front') {
      /* fly */
      g.push('<path d="M' + (cx + 4) + ',30 C' + (cx + 7) + ',' + (rise - 40) + ' ' + (cx + 5) + ',' + (rise - 20) + ' ' + (cx + 2) + ',' + (rise - 6) + '"' + st + dash + ' stroke-width="1.6"></path>');
      g.push('<path class="hsp-ink" d="M' + cx + ',30 C' + (cx + 2) + ',' + (rise - 40) + ' ' + (cx + 1) + ',' + (rise - 18) + ' ' + cx + ',' + (rise - 4) + '" fill="none" stroke-width="1.5"></path>');
      /* J-scoop front pockets: waistband -> curve out and down -> side seam */
      g.push('<path d="M' + (cx - 30) + ',31 C' + (cx - 35) + ',52 ' + (cx - hip + 8) + ',66 ' + (cx - hip + 3) + ',78"' + st + ' stroke-width="1.8"></path>');
      g.push('<path d="M' + (cx + 30) + ',31 C' + (cx + 35) + ',52 ' + (cx + hip - 8) + ',66 ' + (cx + hip - 3) + ',78"' + st + ' stroke-width="1.8"></path>');
      /* coin pocket, tucked inside the right-hand pocket */
      g.push('<path d="M' + (cx + 20) + ',38 h15 v12 h-15 z"' + st + ' stroke-width="1.4"></path>');
    } else {
      /* yoke */
      g.push('<path d="M' + (cx - hip + 2) + ',46 C' + (cx - 30) + ',60 ' + (cx + 30) + ',60 ' + (cx + hip - 2) + ',46"' + st + dash + ' stroke-width="1.6"></path>');
      /* back pockets — the money real estate. Width tracks the hip so a
         WIDE cut does not end up with two postage stamps on it. */
      var pw = Math.round(hip * 0.62), pgap = 5;
      var pl = cx - pgap - pw, pr = cx + pgap;
      g.push('<path d="M' + pl + ',66 h' + pw + ' v26 l' + (-pw / 2) + ',14 l' + (-pw / 2) + ',-14 z"' + st + ' stroke-width="1.9"></path>');
      g.push('<path d="M' + pr + ',66 h' + pw + ' v26 l' + (-pw / 2) + ',14 l' + (-pw / 2) + ',-14 z"' + st + ' stroke-width="1.9"></path>');
      g.push('<path d="M' + (pl + 3) + ',70 h' + (pw - 6) + ' M' + (pr + 3) + ',70 h' + (pw - 6) + '"' + st + ' stroke-width="1.2" opacity="0.75"></path>');
    }

    /* hem chainstitch — follows the clamped leg geometry, so it always
       sits inside the hem no matter how extreme the cut is */
    var L = legs(s);
    var lw = L.hIn - L.hOut, rw = lw;
    g.push('<path d="M' + (L.hOut + 3) + ',' + (GEO.hemY - 13) + ' h' + (lw - 6) +
           ' M' + (200 - L.hIn + 3) + ',' + (GEO.hemY - 13) + ' h' + (rw - 6) + '"' + st + dash + ' stroke-width="1.7"></path>');

    /* hardware — shank button + rivets */
    var hw = hardware || '#c8a24b';
    if (face === 'front') {
      g.push('<circle cx="' + cx + '" cy="20" r="5.2" fill="' + hw + '" stroke="#22150f" stroke-width="1.6"></circle>');
      g.push('<circle cx="' + (cx - 27) + '" cy="46" r="2.6" fill="' + hw + '" stroke="#22150f" stroke-width="1.2"></circle>');
      g.push('<circle cx="' + (cx + 27) + '" cy="46" r="2.6" fill="' + hw + '" stroke="#22150f" stroke-width="1.2"></circle>');
      g.push('<circle cx="' + (cx + 20) + '" cy="41" r="2.2" fill="' + hw + '" stroke="#22150f" stroke-width="1.1"></circle>');
    } else {
      g.push('<circle cx="' + (cx - 44) + '" cy="72" r="2.4" fill="' + hw + '" stroke="#22150f" stroke-width="1.2"></circle>');
      g.push('<circle cx="' + (cx + 44) + '" cy="72" r="2.4" fill="' + hw + '" stroke="#22150f" stroke-width="1.2"></circle>');
    }
    return g.join('');
  }

  function pins(list, face) {
    if (!list || !list.length) { return ''; }
    var table = SPOTS[face] || SPOTS.front;
    var used = {};
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var title = String(list[i] || '');
      var spot = table.DEFAULT, key;
      for (key in table) {
        if (key !== 'DEFAULT' && has(title, key)) { spot = table[key]; break; }
      }
      /* fan out pins that share a spot so none hides another */
      var sig = spot[0] + ':' + spot[1];
      var n = used[sig] || 0;
      used[sig] = n + 1;
      var x = spot[0] + (n % 2 === 0 ? 0 : 15) + Math.floor(n / 2) * 4;
      var y = spot[1] + Math.floor(n / 2) * 15;
      out.push('<g class="hsp-pin" transform="translate(' + x + ',' + y + ')">' +
               '<circle r="9.5" fill="#ff2f52" stroke="#22150f" stroke-width="2.4"></circle>' +
               '<circle r="3.4" fill="#fff"></circle>' +
               '<title>' + esc(title) + '</title></g>');
    }
    return out.join('');
  }

  function esc(t) {
    return String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /* ── render ────────────────────────────────────────────────────────── */
  function render(host, state) {
    var id = host.getAttribute('data-hsp-uid');
    if (!id) { id = String(++uid); host.setAttribute('data-hsp-uid', id); }

    var face = state.face === 'back' ? 'back' : 'front';
    var s = pickShape(state.cutTitle, state.cutSku, state.cutConfig);
    var fab = pickFabric(state.fabricTitle, state.fabricConfig);
    var body = bodyPath(s);
    var stitch = state.stitch || '#e8d9a8';

    var svg = [];
    svg.push('<svg class="hsp-svg" viewBox="0 0 200 336" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="' +
             esc(previewLabel(state, face)) + '">');
    svg.push(defs(id, fab, state.swatchUrl, body));
    /* cloth */
    svg.push('<path d="' + body + '" fill="url(#hsp-fill-' + id + ')"></path>');
    svg.push('<g clip-path="url(#hsp-clip-' + id + ')">');
    svg.push('<rect x="0" y="0" width="200" height="336" filter="url(#hsp-slub-' + id + ')" opacity="0.13"></rect>');
    if (fab.wash > 0.02) {
      var o = (fab.wash * 0.9).toFixed(2);
      svg.push('<ellipse cx="72" cy="112" rx="34" ry="20" fill="url(#hsp-fade-' + id + ')" opacity="' + o + '"></ellipse>');
      svg.push('<ellipse cx="128" cy="112" rx="34" ry="20" fill="url(#hsp-fade-' + id + ')" opacity="' + o + '"></ellipse>');
      svg.push('<ellipse cx="70" cy="192" rx="26" ry="26" fill="url(#hsp-fade-' + id + ')" opacity="' + o + '"></ellipse>');
      svg.push('<ellipse cx="130" cy="192" rx="26" ry="26" fill="url(#hsp-fade-' + id + ')" opacity="' + o + '"></ellipse>');
    }
    svg.push('<rect x="0" y="0" width="200" height="336" fill="url(#hsp-shade-' + id + ')"></rect>');
    svg.push('</g>');
    /* outline + details */
    svg.push('<path class="hsp-ink" d="' + body + '" fill="none" stroke-width="3.4" stroke-linejoin="round"></path>');
    svg.push('<g clip-path="url(#hsp-clip-' + id + ')">' + details(s, face, stitch, state.hardware) + '</g>');
    svg.push('<g class="hsp-pins">' + pins(state.addons, face) + '</g>');
    svg.push('</svg>');

    host.innerHTML = svg.join('');
  }

  function previewLabel(state, face) {
    var bits = [];
    bits.push(face === 'back' ? 'Back view' : 'Front view');
    if (state.cutTitle) { bits.push(state.cutTitle); }
    if (state.fabricTitle) { bits.push('in ' + state.fabricTitle); }
    if (state.addons && state.addons.length) { bits.push('with ' + state.addons.length + ' customization'); }
    return bits.join(', ');
  }

  /* ── read the current state straight off the builder inputs ────────── */
  function readState(scope, host) {
    var st = { face: host.getAttribute('data-hsp-face') || 'front', addons: [] };

    var cut = scope.querySelector('[data-hsb-cut]:checked');
    if (cut) {
      st.cutTitle = cut.getAttribute('data-title');
      st.cutSku = cut.getAttribute('data-sku');
      st.cutConfig = parseJSON(cut.getAttribute('data-config'));
    }

    var fab = scope.querySelector('[data-hsb-fabric]:checked');
    if (fab) {
      st.fabricTitle = fab.getAttribute('data-title');
      st.fabricConfig = parseJSON(fab.getAttribute('data-fabric-config'));
      st.swatchUrl = fab.getAttribute('data-swatch') || '';
    }

    /* topstitch: the HEM placement drives the visible thread colour */
    var thread = scope.querySelector('[data-hsb-thread-placement] input:checked');
    if (thread) {
      var sw = thread.parentNode.querySelector('.hsc__opt-swatch');
      if (sw && sw.style && sw.style.backgroundColor) { st.stitch = sw.style.backgroundColor; }
    }

    var hw = scope.querySelector('[data-hsb-hwset]:checked');
    if (hw) {
      var hwt = hw.getAttribute('data-title') || '';
      if (has(hwt, 'BLACK') || has(hwt, 'MATTE')) { st.hardware = '#2b2b30'; }
      else if (has(hwt, 'SILVER') || has(hwt, 'NICKEL')) { st.hardware = '#cfd4da'; }
      else if (has(hwt, 'COPPER')) { st.hardware = '#c9743f'; }
      else { st.hardware = '#c8a24b'; }
    }

    var boxes = scope.querySelectorAll('[data-hsc-addon]:checked');
    for (var i = 0; i < boxes.length; i++) {
      st.addons.push(boxes[i].getAttribute('data-title') || '');
    }
    return st;
  }

  function parseJSON(raw) {
    if (!raw) { return null; }
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  /* ── wiring ────────────────────────────────────────────────────────── */
  function scopeOf(host) {
    return host.closest('.hsc') || host.closest('.hsdb') || document;
  }

  /* Swatch chips are painted from the SAME colour table that fills the
     jean, so a chip can never disagree with the garment beside it. A real
     swatch photo wins here too. */
  function paintSwatches(root) {
    var els = root.querySelectorAll('[data-hsp-swatch]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var url = el.getAttribute('data-swatch');
      if (url) {
        el.style.backgroundImage = 'url(' + url + ')';
        el.style.backgroundSize = 'cover';
        el.style.backgroundPosition = 'center';
        continue;
      }
      var fab = pickFabric(el.getAttribute('data-title'), parseJSON(el.getAttribute('data-fabric-config')));
      el.style.backgroundImage = 'repeating-linear-gradient(63deg, ' +
        fab.hi + ' 0 2px, ' + fab.base + ' 2px 5px, ' + fab.lo + ' 5px 6px)';
    }
  }

  function boot() {
    paintSwatches(document);
    var hosts = document.querySelectorAll('[data-hsp]');
    for (var i = 0; i < hosts.length; i++) {
      (function (host) {
        var scope = scopeOf(host);
        var draw = function () { render(host, readState(scope, host)); };

        scope.addEventListener('change', draw);

        var toggles = scope.querySelectorAll('[data-hsp-toggle]');
        for (var j = 0; j < toggles.length; j++) {
          toggles[j].addEventListener('click', function (ev) {
            var want = ev.currentTarget.getAttribute('data-hsp-toggle');
            host.setAttribute('data-hsp-face', want);
            var all = scope.querySelectorAll('[data-hsp-toggle]');
            for (var k = 0; k < all.length; k++) {
              all[k].setAttribute('aria-pressed', all[k] === ev.currentTarget ? 'true' : 'false');
            }
            draw();
          });
        }
        draw();
        host.hspRedraw = draw;
      })(hosts[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.HSJeanPreview = {
    render: render, boot: boot, paintSwatches: paintSwatches,
    SHAPES: SHAPES, FABRICS: FABRICS,
    // Exported so hs-flat-cloth.js can colour the traced flat using exactly the
    // same fabric resolution (keyword match plus Admin fabric_config overrides)
    // instead of keeping a second copy of the mapping that could drift.
    pickFabric: pickFabric
  };
})();
